import axios from 'axios';
import { authService } from './authService';

// Update this with your Railway backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ===========================
// GLOBAL ERROR HANDLER
// ===========================

let globalErrorHandler = null;

export const setGlobalErrorHandler = (handler) => {
  globalErrorHandler = handler;
  console.log('✅ Global error handler set:', !!handler);
};

// ===========================
// AXIOS INSTANCE
// ===========================

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ===========================
// REQUEST INTERCEPTOR
// ===========================

api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ===========================
// RESPONSE INTERCEPTOR
// ===========================

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Normalize the error to consistent format
    const normalizedError = normalizeError(error);
    
    // Call global error handler if exists
    if (globalErrorHandler) {
      globalErrorHandler(normalizedError);
    }
    
    return Promise.reject(normalizedError);
  }
);

// ===========================
// ERROR NORMALIZATION
// ===========================

/**
 * Normalize error to consistent format
 * Handles ALL FastAPI HTTPException formats
 */
const normalizeError = (error) => {
  const timestamp = new Date().toISOString();

  // Handle network errors (no response from server)
  if (!error.response) {
    return {
      status: 0,
      statusText: 'Network Error',
      message: error.message || 'Unable to connect to the server. Please check your internet connection.',
      detail: null,
      isNetworkError: true,
      timestamp,
    };
  }

  const { status, statusText, data } = error.response;

  // Initialize normalized structure
  let normalizedDetail = null;
  let message = 'An unexpected error occurred';

  // Handle all error formats
  if (data) {
    if (typeof data === 'string') {
      message = data;
    } else if (typeof data === 'object' && data !== null) {
      if (data.detail !== undefined) {
        
        // CASE 1: Simple string detail
        if (typeof data.detail === 'string') {
          message = data.detail;
        }
        
        // CASE 2: Structured error object (like credits error)
        else if (typeof data.detail === 'object' && data.detail !== null && !Array.isArray(data.detail)) {
          normalizedDetail = {
            error: safeString(data.detail.error),
            message: safeString(data.detail.message),
            credits_needed: safeNumber(data.detail.credits_needed),
            credits_available: safeNumber(data.detail.credits_available),
            shortage: safeNumber(data.detail.shortage),
            ...extractAdditionalFields(data.detail)
          };
          
          message = normalizedDetail.message || normalizedDetail.error || 'An error occurred';
        }
        
        // CASE 3: Array detail (FastAPI validation errors)
        else if (Array.isArray(data.detail)) {
          message = 'Validation error';
          normalizedDetail = data.detail;
        }
      } else {
        message = data.message || data.error || JSON.stringify(data);
        normalizedDetail = data;
      }
    }
  }

  // Fallback to default messages if still generic
  if (!message || message === 'An unexpected error occurred') {
    message = getDefaultMessageForStatus(status);
  }

  return {
    status,
    statusText,
    message,
    detail: normalizedDetail,
    isNetworkError: false,
    timestamp,
  };
};

const extractAdditionalFields = (detail) => {
  const knownFields = ['error', 'message', 'credits_needed', 'credits_available', 'shortage'];
  const additionalFields = {};
  
  Object.keys(detail).forEach((key) => {
    if (!knownFields.includes(key)) {
      additionalFields[key] = detail[key];
    }
  });
  
  return additionalFields;
};

const getDefaultMessageForStatus = (status) => {
  const messages = {
    400: 'Bad request. Please check your input.',
    401: 'Unauthorized. Please log in again.',
    403: 'Access denied. You don\'t have permission to perform this action.',
    404: 'The requested resource was not found.',
    402: 'Payment required. Insufficient credits.',
    409: 'Conflict. The resource already exists.',
    422: 'Validation error. Please check your input.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error. Please try again later.',
    502: 'Bad gateway. The server is temporarily unavailable.',
    503: 'Service unavailable. Please try again later.',
  };
  
  return messages[status] || `Error ${status}: ${getStatusText(status)}`;
};

const getStatusText = (status) => {
  if (status >= 500) return 'Server Error';
  if (status >= 400) return 'Client Error';
  if (status >= 300) return 'Redirection';
  if (status >= 200) return 'Success';
  return 'Unknown';
};

const safeString = (value) => {
  if (value === null || value === undefined) return null;
  return String(value);
};

const safeNumber = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

// ===========================
// HELPER FUNCTIONS
// ===========================

const getAuthHeaders = () => {
  const token = authService.getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const handleResponse = async (response) => {
  if (response.status === 401) {
    authService.logout();
    window.location.href = '/signin';
    throw new Error('Authentication required');
  }
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// ===========================
// FETCH WITH ERROR HANDLING
// ===========================

const fetchWithErrorHandling = async (url, options = {}) => {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.log('🔴 Error response text:', errorText);
      
      let errorData;
      
      try {
        errorData = JSON.parse(errorText);
        console.log('📦 Parsed error data:', errorData);
      } catch {
        errorData = { detail: errorText };
      }
      
      // Create normalized error
      const normalizedError = {
        status: response.status,
        statusText: response.statusText,
        message: '',
        detail: null,
        isNetworkError: false,
        timestamp: new Date().toISOString()
      };

      // Handle different detail formats
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          normalizedError.message = errorData.detail;
        } else if (typeof errorData.detail === 'object') {
          normalizedError.detail = errorData.detail;
          normalizedError.message = errorData.detail.message || errorData.detail.error || 'Request failed';
        }
      } else {
        normalizedError.message = errorData.message || 'Request failed';
      }

      console.log('🔔 Normalized error:', normalizedError);

      // Trigger global error handler
      if (globalErrorHandler) {
        console.log('📢 Calling global error handler');
        globalErrorHandler(normalizedError);
      } else {
        console.warn('⚠️ No global error handler set!');
      }
      
      // ✅ FIXED: Throw the normalized error object, not just the message
      const error = new Error(normalizedError.message);
      error.status = normalizedError.status;
      error.detail = normalizedError.detail;
      error.statusText = normalizedError.statusText;
      error.timestamp = normalizedError.timestamp;
      throw error;
    }
    
    return response;
  } catch (error) {
    // Network errors (only if not already a structured error)
    if (!error.status && !error.response && globalErrorHandler && error.message !== 'Authentication required') {
      const networkError = {
        status: 0,
        detail: {
          error: 'Network Error',
          message: 'Unable to connect to server. Please check your internet connection.'
        },
        message: 'Network error occurred',
        timestamp: new Date().toISOString()
      };
      
      globalErrorHandler(networkError);
      
      // Attach structured error info
      error.status = networkError.status;
      error.detail = networkError.detail;
    }
    throw error;
  }
};

// ===========================
// FEATURED VIDEOS
// ===========================

export const getFeaturedVideos = async () => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/featured-videos`);
  return response.json();
};

// ===========================
// CODE GENERATION
// ===========================

export const generateCode = async (prompt) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-code`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ prompt })
  });
  
  return handleResponse(response);
};

// ===========================
// PIXEL AI TUTOR - PRACTICE PROBLEMS
// ===========================

export const getPracticeProblemsForQuestion = async (questionId) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/questions/${questionId}/practice-problems`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const getPracticeProblemsForSolution = async (solutionId) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/solutions/${solutionId}/practice-problems`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const renderVideoFromSolution = async (solutionId, quality = 'qm', aspectRatio = '16:9', language = 'en') => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/render-from-solution/${solutionId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      quality,
      aspect_ratio: aspectRatio
    })
  });
  
  return handleResponse(response);
};

export const getSolution = async (solutionId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/solutions/${solutionId}`, {
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

export const generateSimilarProblems = async (solutionId, count = 3, difficultyLevels = ['easy', 'medium', 'hard']) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-similar-problems`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      solution_id: solutionId,
      count,
      difficulty_levels: difficultyLevels
    })
  });
  
  return handleResponse(response);
};

export const getPracticeProblems = async (filters = {}) => {
  const {
    subject = null,
    difficulty = null,
    tags = null,
    limit = 20,
    offset = 0
  } = filters;
  
  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  if (subject) queryParams.append('subject', subject);
  if (difficulty) queryParams.append('difficulty', difficulty);
  if (tags) queryParams.append('tags', tags);
  
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/practice-problems?${queryParams}`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const markPracticeProblemSolved = async (problemId, isCorrect, timeSeconds) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/practice-problems/${problemId}/solve`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        is_correct: isCorrect,
        time_seconds: timeSeconds
      })
    }
  );
  
  return handleResponse(response);
};

// ===========================
// VIDEO GENERATION
// ===========================

export const generateVideoFromCode = async (sceneName, code, language, quality, aspectRatio = '16:9', duration = 60) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-video-from-code`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      scene_name: sceneName,
      code,
      language,
      quality,
      aspect_ratio: aspectRatio,
      duration
    })
  });
  
  return handleResponse(response);
};

export const generateVideo = async (prompt, quality, language, aspectRatio = '16:9', duration = 60) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-video`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      prompt,
      quality,
      language,
      aspect_ratio: aspectRatio,
      duration
    })
  });
  
  return response.json();
};

// ===========================
// VIDEO MANAGEMENT
// ===========================

export const getVideos = async (limit = 50, offset = 0) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/videos?limit=${limit}&offset=${offset}`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const getVideoDetails = async (videoId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}/details`, {
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

export const submitJob = async (jobId, script) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/submit-job`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      job_id: jobId,
      script
    })
  });

  return handleResponse(response);
};

export const deleteVideo = async (videoId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });

  return handleResponse(response);
};

export const setVideoPublic = async (videoId, isPublic) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}/visibility`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_public: isPublic })
  });
  return handleResponse(response);
};

export const getVideoStatus = async (videoId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/status/${videoId}`, {
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

// ===========================
// WORKER STATUS
// ===========================

export const getWorkerStatus = async () => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/worker-status`);
  return handleResponse(response);
};

export const getVideoQuotaStatus = async () => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/video-quota-status`, {
    headers: getAuthHeaders()
  });
  return handleResponse(response);
};

// ===========================
// PUBLIC GALLERY
// ===========================

export const fetchPublicVideos = async (params = {}) => {
  const {
    page = 1,
    limit = 12,
    sort = 'recent',
    subject = 'all',
    language = 'all',
    search = ''
  } = params;
  
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sort
  });
  
  if (subject && subject !== 'all') {
    queryParams.append('subject', subject);
  }
  
  if (language && language !== 'all') {
    queryParams.append('language', language);
  }
  
  if (search && search.trim()) {
    queryParams.append('search', search.trim());
  }
  
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/videos/public/test?${queryParams}`,
    { headers: { 'Content-Type': 'application/json' } }
  );
  
  return handleResponse(response);
};

export const incrementVideoView = async (videoId) => {
  try {
    const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}/view`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      console.warn('Failed to increment view count for video:', videoId);
      return null;
    }
    
    return response.json();
  } catch (error) {
    console.warn('Error incrementing view count:', error);
    return null;
  }
};

export const reactToVideo = async (videoId, reaction) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}/react`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ reaction }),
  });
  return handleResponse(response);
};

export const getVideoReactions = async (videoId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/videos/${videoId}/reactions`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getPublicVideoDetails = async (videoId) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/videos/public/${videoId}/details`,
    { headers: { 'Content-Type': 'application/json' } }
  );
  
  return handleResponse(response);
};

// ===========================
// CREDIT STATUS & TRANSACTIONS
// ===========================

export const getCreditStatus = async () => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/credit-status`, {
    headers: getAuthHeaders()
  });
  
  return handleResponse(response);
};

export const getCreditTransactions = async (params = {}) => {
  const { limit = 50, offset = 0 } = params;

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  });

  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/credit-transactions?${queryParams}`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const getCreditHistory = async (params = {}) => {
  const { page = 1, limit = 10 } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });

  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/credits/history?${queryParams}`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const checkVideoGeneration = async (durationSeconds) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/check-video-generation`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ duration_seconds: durationSeconds })
  });
  
  return handleResponse(response);
};

export const calculateCreditsNeeded = (durationSeconds) => {
  return Math.ceil(durationSeconds / 30);
};

export const formatCredits = (credits) => {
  if (credits === 1) return '1 credit';
  return `${credits} credits`;
};

export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const getPlanDisplayName = (plan) => {
  const names = {
    'free': 'Free',
    'hobby': 'Hobby',
    'pro': 'Pro'
  };
  return names[plan] || plan;
};

export const getPlanColor = (plan) => {
  const colors = {
    'free': '#10b981',
    'hobby': '#3b82f6',
    'pro': '#ef4444'
  };
  return colors[plan] || '#6b7280';
};

// ===========================
// QUESTION HISTORY & TRANSCRIPTS
// ===========================

export const getQuestionHistory = async (filters = {}) => {
  const {
    subject = 'all',
    days = 30,
    limit = 50,
    offset = 0
  } = filters;
  
  const queryParams = new URLSearchParams({
    days: days.toString(),
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  if (subject && subject !== 'all') {
    queryParams.append('subject', subject);
  }
  
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/question-history?${queryParams}`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

export const downloadQuestionTranscript = async (questionId) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/question-history/${questionId}/download`,
    { headers: { 'Authorization': `Bearer ${authService.getToken()}` } }
  );
  
  if (!response.ok) {
    throw new Error('Failed to download transcript');
  }
  
  return response.blob();
};

export const downloadQuestionHistory = async (downloadType = 'all', filters = {}) => {
  if (downloadType === 'all') {
    const queryParams = new URLSearchParams();
    
    if (filters.subject && filters.subject !== 'all') {
      queryParams.append('subject', filters.subject);
    }
    
    if (filters.days) {
      queryParams.append('days', filters.days.toString());
    }
    
    const response = await fetchWithErrorHandling(
      `${API_BASE_URL}/api/question-history/download-all?${queryParams}`,
      { headers: { 'Authorization': `Bearer ${authService.getToken()}` } }
    );
    
    if (!response.ok) {
      throw new Error('Failed to download all transcripts');
    }
    
    return response.blob();
  } else {
    return downloadQuestionTranscript(downloadType);
  }
};

// ===========================
// PRACTICE PROBLEM SOLUTIONS
// ===========================

export const submitPracticeSolution = async (data) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/practice-solutions`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        problem_id: data.problem_id,
        user_solution: data.user_solution,
        is_correct: data.is_correct,
        time_seconds: data.time_seconds
      })
    }
  );
  
  return handleResponse(response);
};

export const getPracticeSolutionHistory = async (problemId) => {
  const response = await fetchWithErrorHandling(
    `${API_BASE_URL}/api/practice-solutions/${problemId}/history`,
    { headers: getAuthHeaders() }
  );
  
  return handleResponse(response);
};

// ===========================
// SOLUTION GENERATION
// ===========================

export const generateSolution = async (question, language = 'fi', duration = 60, subject = 'chemistry') => {
  console.log('🔍 generateSolution called with:', { question, language, duration, subject });
  
  const requestBody = {
    question,
    language,
    duration,
    subject
  };
  
  try {
    const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-solution`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });
    
    const data = await response.json();
    console.log('✅ Success response:', data);
    return data;
    
  } catch (error) {
    console.error('❌ Catch block error:', error);
    throw error;
  }
};

export const generateSolutionV2 = async (
  question,
  language = 'fi',
  duration = 60,
  subject = 'chemistry',
  imageUrl = null
) => {
  console.log('🔍 generateSolutionV2 called with:', { question, language, duration, subject, imageUrl });

  const requestBody = {
    question,
    language,
    duration,
    subject,
  };

  if (imageUrl) {
    requestBody.image_url = imageUrl;
  }

  try {
    const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/generate-solution-v2`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    console.log('✅ Success response:', data);
    return data;

  } catch (error) {
    console.error('❌ Catch block error:', error);
    throw error;
  }
};

// ===========================
// PIXEL AI CHAT (CONVERSATIONAL)
// ===========================

export const pixelChat = async ({ sessionId = null, message = '', subject = 'chemistry', language = 'fi', imageUrl = null, conceptId = null, courseId = null, isOpener = false, studySetId = null, passageContext = null, passageImageUrl = null }) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/pixel-chat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      session_id: sessionId,
      message,
      subject,
      language,
      image_url: imageUrl,
      concept_id: conceptId,
      course_id: courseId,
      is_opener: isOpener,
      study_set_id: studySetId,
      passage_context: passageContext,
      passage_image_url: passageImageUrl,
    }),
  });
  return handleResponse(response);
};

export const getPixelSessions = async () => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/pixel-sessions`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const getPixelSession = async (sessionId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/pixel-sessions/${sessionId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

export const deletePixelSession = async (sessionId) => {
  const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/pixel-sessions/${sessionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

// ===========================
// IMAGE UPLOAD
// ===========================

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Send support request with optional image attachment
 */
export const sendSupportRequest = async (data) => {
  try {
    const response = await api.post('/api/support/send', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadImageBase64 = async (file) => {
  console.log('🖼️ Starting base64 image upload', {
    name: file.name,
    type: file.type,
    size: file.size
  });

  try {
    console.log('📝 Converting to base64...');
    const base64Data = await fileToBase64(file);
    
    console.log('📤 Sending to backend...');
    
    const response = await fetchWithErrorHandling(`${API_BASE_URL}/api/upload-image-base64`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        image_base64: base64Data,
        content_type: file.type
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Upload failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Image uploaded successfully:', data.public_url);
    
    return data.public_url;

  } catch (error) {
    console.error('❌ Image upload failed:', error);
    throw error;
  }
};

export const uploadImageToCDN = async (file) => {
  const res = await fetchWithErrorHandling(`${API_BASE_URL}/api/image-upload-url`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      content_type: file.type
    })
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  const { upload_url, public_url } = await res.json();

  const uploadRes = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type
    }
  });

  if (!uploadRes.ok) {
    throw new Error('Image upload to R2 failed');
  }

  return public_url;
};

// ===========================
// HELPER FUNCTIONS
// ===========================

export const formatTimeSpent = (seconds) => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

export const calculateAccuracy = (correct, total) => {
  if (total === 0) return 0;
  return Math.round((correct / total) * 100);
};

export const getSubjectIcon = (subject) => {
  const icons = {
    'chemistry': '🧪',
    'physics': '⚡',
    'mathematics': '📐'
  };
  return icons[subject] || '📚';
};

export const getDifficultyColor = (difficulty) => {
  const colors = {
    'easy': '#10b981',
    'medium': '#f59e0b',
    'hard': '#ef4444'
  };
  return colors[difficulty] || '#6b7280';
};

// ===========================
// TEST FUNCTIONS
// ===========================

export const testErrorDialog = () => {
  if (globalErrorHandler) {
    globalErrorHandler({
      status: 402,
      detail: {
        error: 'Insufficient credits',
        message: 'You need 3 more credits to complete this operation.',
        credits_needed: 5,
        credits_available: 2,
        shortage: 3
      },
      message: 'You need 3 more credits to complete this operation.',
      timestamp: new Date().toISOString()
    });
    console.log('✅ Test error dialog triggered');
  } else {
    console.error('❌ Global error handler not set!');
  }
};

// Test different error types
export const testNetworkError = () => {
  if (globalErrorHandler) {
    globalErrorHandler({
      status: 0,
      detail: {
        error: 'Network Error',
        message: 'Unable to connect to server. Please check your internet connection.'
      },
      message: 'Network error occurred',
      timestamp: new Date().toISOString()
    });
    console.log('✅ Network error dialog triggered');
  }
};

export const testAuthError = () => {
  if (globalErrorHandler) {
    globalErrorHandler({
      status: 401,
      detail: 'Unauthorized. Please log in again.',
      message: 'Unauthorized. Please log in again.',
      timestamp: new Date().toISOString()
    });
    console.log('✅ Auth error dialog triggered');
  }
};

export const testServerError = () => {
  if (globalErrorHandler) {
    globalErrorHandler({
      status: 500,
      detail: 'Internal server error occurred',
      message: 'Server error. Please try again later.',
      timestamp: new Date().toISOString()
    });
    console.log('✅ Server error dialog triggered');
  }
};

export default api;